import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import isNetworkError from 'is-network-error';
import type { UserSegment } from '../types/admin';
import { externalWritesStubbed } from './externalWrites';

// @ts-ignore - shared .mjs module used across frontend and lambdas
import { POSTHOG_TOKEN, POSTHOG_HOST } from '../../../shared/posthog-config.mjs';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || POSTHOG_TOKEN;

let isInitialized = false;

/**
 * Markers for exceptions raised outside the application: the Microsoft link
 * scanner artifact (Outlook/Teams SafeLinks prefetch) injected into the page by
 * the mail client, and the MetaMask browser extension's injected provider
 * failing to connect its wallet. Neither is thrown by application code, and no
 * app operation depends on them, so they are noise in every form they arrive in.
 */
const THIRD_PARTY_NOISE_MARKERS = ['Object Not Found Matching', 'Failed to connect to MetaMask'];

function isThirdPartyNoiseMessage(text: unknown): boolean {
  return typeof text === 'string' && THIRD_PARTY_NOISE_MARKERS.some((m) => text.includes(m));
}

/**
 * Suppress third-party noise exceptions at the point every capture path shares.
 *
 * The filter in captureError() only covers the manual window handlers in
 * main.tsx. The SDK's own exception autocapture reports unhandled rejections
 * straight to PostHog without going through captureError(), so `before_send` is
 * the only hook that can see both paths. Autocapture carries the message in
 * `$exception_list[].value` rather than `$exception_message`.
 */
export function dropThirdPartyNoiseExceptions(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;

  const properties = event.properties ?? {};
  const candidates: unknown[] = [properties.$exception_message, properties.$exception_type];

  if (Array.isArray(properties.$exception_list)) {
    for (const exception of properties.$exception_list) {
      candidates.push(exception?.value, exception?.type);
    }
  }

  if (candidates.some(isThirdPartyNoiseMessage)) {
    console.debug('[PostHog] Dropping third-party noise exception');
    return null;
  }

  return event;
}

export function initPostHog() {
  // Only initialize in production, and never on a build that stubs external writes, whose events
  // would land in the production project.
  if (!POSTHOG_KEY || !import.meta.env.PROD || externalWritesStubbed()) {
    console.log('[PostHog] Disabled in development');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    capture_performance: true,
    before_send: dropThirdPartyNoiseExceptions,
  });

  isInitialized = true;
}

export interface ActiveBranchData {
  branch_id: string;
  // Always a string: the branch list types this as a number while the column and the user
  // profile are strings, and a PostHog property whose type varies can't be filtered on.
  branch_code: string;
  branch_name: string;
  cohort?: string | null;
  branch_status?: string;
  is_override: boolean;
}

export function identifyUser(email: string, name?: string) {
  if (!isInitialized) return;
  posthog.identify(email, { name, email });
}

export function setActiveBranch(branchData: ActiveBranchData) {
  if (!isInitialized) return;
  posthog.register({
    active_branch_id: branchData.branch_id,
    active_branch_code: branchData.branch_code,
    active_branch_name: branchData.branch_name,
    active_cohort: branchData.cohort,
    active_branch_status: branchData.branch_status,
    is_impersonating: branchData.is_override,
  });
}

export function setUserSegment(segment: UserSegment) {
  if (!isInitialized) return;
  // A super property so every event the user produces is segmentable, and a person property so
  // person-level breakdowns and cohorts work from the same value.
  posthog.register({ segment });
  posthog.setPersonProperties({ segment });
}

/**
 * Drop everything that describes who is signed in and which branch they were looking at.
 * Deliberately leaves the login day-stamp alone — signing out and back in on the same day is
 * still one login.
 */
export function clearBranchContext() {
  if (!isInitialized) return;
  posthog.unregister('active_branch_id');
  posthog.unregister('active_branch_code');
  posthog.unregister('active_branch_name');
  posthog.unregister('active_cohort');
  posthog.unregister('active_branch_status');
  posthog.unregister('is_impersonating');
  posthog.unregister('segment');
}

export function captureEvent(event: string, properties?: Record<string, any>) {
  if (!isInitialized) return;

  posthog.capture(event, properties);
}

export function captureError(error: any, context?: Record<string, any>) {
  if (!isInitialized) return;

  // Filter out network errors using is-network-error library
  if (isNetworkError(error)) {
    console.debug('[PostHog] Skipping network error:', error.message);
    return;
  }

  // Filter out third-party noise (Outlook/Teams link prefetch, MetaMask extension)
  const errorMessage = error?.message || String(error);
  if (isThirdPartyNoiseMessage(errorMessage)) {
    console.debug('[PostHog] Skipping third-party noise error:', errorMessage);
    return;
  }

  posthog.capture('$exception', {
    $exception_type: error.name,
    $exception_message: error.message,
    $exception_stack: error.stack,
    ...context,
  });
}

/**
 * Read the monitoring correlationId off the URL (`?_diag_crash=<id>`), if the
 * page was loaded by a synthetic crash probe. The global window error handler
 * forwards it onto the reported `$exception`; its presence marks the event
 * synthetic (real crashes never carry one).
 */
export function diagCorrelationId(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('_diag_crash');
}

/**
 * Front-end crash diagnostic trigger.
 *
 * When the page is loaded with `?_diag_crash=<correlationId>`, deliberately
 * raise an uncaught error so the monitoring-validation tool can prove the
 * deployed bundle reports browser crashes to PostHog. It does NOT report the
 * error itself — it only throws, so the global `window` error handler in
 * main.tsx is the thing that reports (forwarding the correlationId). If that
 * handler is missing or broken, no event lands and the test fails, which is the
 * point.
 *
 * Call once at startup, after initPostHog().
 */
export function runDiagCrashIfRequested() {
  if (!diagCorrelationId()) return;

  const error = new Error('Synthetic front-end crash from monitoring diagnostic trigger');
  error.name = 'SyntheticDiagnosticCrash';
  // Throw asynchronously so it surfaces through window.onerror as a genuine
  // uncaught error, without aborting module initialization synchronously.
  setTimeout(() => {
    throw error;
  }, 0);
}

/** Find a branch by its id. Trimmed-string comparison so a stray whitespace can't miss. */
export function findBranchById<T extends { id: string }>(branches: T[], branchId: string): T | undefined {
  const wanted = String(branchId).trim();
  if (!wanted) return undefined;
  return branches.find(b => String(b.id).trim() === wanted);
}

/**
 * Register the branch a user is looking at. Keyed on the Branch id, which is the only identifier
 * both the user profile and the branch switcher carry — the branch_code is a display value and
 * arrives typed as a number in one place and a string in the other.
 */
export async function setBranchContext(branchId: string, isOverride: boolean = false) {
  if (!isInitialized) return;

  try {
    const { franchiseApi } = await import('./franchiseApi');
    const agenciesRes = await franchiseApi.getAgencies();
    const branch = findBranchById(agenciesRes.branches, branchId);

    if (!branch) {
      // The profile names a branch the branch list doesn't have. Registering nothing and staying
      // silent leaves broken branch filtering indistinguishable from a user who has no branch,
      // so make the failure queryable instead.
      captureEvent('branch_context_unresolved', {
        branch_id: String(branchId),
        is_impersonating: isOverride,
      });
      return;
    }

    const branchData: ActiveBranchData = {
      branch_id: branch.id,
      branch_code: String(branch.branch_code),
      branch_name: branch.reporting_name || branch.dba_name,
      cohort: branch.cohort,
      branch_status: branch.status,
      is_override: isOverride,
    };
    setActiveBranch(branchData);

    // Person properties describe the user's own branch, so an impersonated branch must not
    // overwrite them — only the super properties follow the branch switcher.
    if (!isOverride) {
      posthog.setPersonProperties({
        branch_id: branchData.branch_id,
        branch_code: branchData.branch_code,
        branch_name: branchData.branch_name,
        cohort: branchData.cohort,
        branch_status: branchData.branch_status,
      });
    }
  } catch (error) {
    console.debug('[PostHog] Failed to update branch context:', error);
  }
}

// A login is a user's first authenticated app session on a calendar day. Both auth providers
// keep tokens in localStorage and the app restores its session from there, so the interactive
// sign-in prompt fires on a schedule set by each IdP's token lifetime rather than by the user —
// counting handshakes would compare Microsoft users against Okta users and measure the config,
// not the person. The day is resolved in the PostHog project's own timezone so that one event
// per user per day here is also one event per user per day in a PostHog insight.
const LOGIN_DAY_KEY = 'posthog-login-day';
const LOGIN_DAY_TIMEZONE = 'America/New_York';

/** Today's date as `YYYY-MM-DD` in the login timezone. `now` is injectable for tests. */
export function loginDayStamp(now: Date = new Date()): string {
  // en-CA renders ISO-ordered dates, so the stamp compares as a plain string.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: LOGIN_DAY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** Record `day` as the user's login day, returning false if it was already recorded. */
export function claimLoginDay(day: string): boolean {
  try {
    if (localStorage.getItem(LOGIN_DAY_KEY) === day) return false;
    localStorage.setItem(LOGIN_DAY_KEY, day);
    return true;
  } catch {
    // Storage unavailable (private mode, quota, blocked): counting the session is a better
    // failure than dropping the user's logins entirely.
    return true;
  }
}

/**
 * Emit the `login` event, at most once per user per day. Safe to call on every app boot — the
 * day-stamp, not the caller, is what enforces the count.
 */
export function captureLoginOncePerDay(properties?: Record<string, any>, now: Date = new Date()) {
  if (!isInitialized) return;

  const day = loginDayStamp(now);
  if (!claimLoginDay(day)) return;

  captureEvent('login', { ...properties, login_day: day });
}

export { posthog };
